import './Form.css'
import { useState } from 'react'
import PageSelection from './PageSelection'
import TermsCheckbox from './TermsCheckbox'
import EmailInput from './EmailInput'
import Captcha from '../Captcha/Captcha'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'

const Form = ({
  objectId,
  onSuccess,
  onError,
  numberOfScans,
  startAndEndCanvas,
  config,
}) => {
  const [email, setEmail] = useState('')
  const [pages, setPages] = useState({
    all: true,
    from: startAndEndCanvas.start,
    to: startAndEndCanvas.end,
  })
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [errors, setErrors] = useState({ email: '', pages: '', terms: '' })

  const [isCaptchaValid, setIsCaptchaValid] = useState(false)
  const [captchaToken, setCaptchaToken] = useState(null)

  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    const emailError = validateEmail(email)
      ? ''
      : 'Bitte geben Sie eine gültige E-Mail Adresse ein.'
    const pagesError = validatePages(pages)
      ? ''
      : 'Bitte geben Sie einen gültigen Seitenbereich ein.'
    const termsError = validateTerms(isTermsAccepted)
      ? ''
      : 'Bitte akzeptieren Sie die Nutzungsbedingungen.'

    if (!emailError && !pagesError && !termsError && isCaptchaValid) {
      setLoading(true)
      try {
        const apiUrl = process.env.REACT_APP_ENV === "local" 
        ? "http://dev-cluster1.onb.ac.at:30518/api/order" 
        : "/api/order";

        await postPdfRequest(apiUrl, objectId, email, pages, captchaToken)
        onSuccess(email, pages)
      } catch (error) {
        console.error('Error submitting form:', error.message)
        onError(error.message)
      } finally {
        setLoading(false)
      }
    } else {
      setErrors({
        email: emailError,
        pages: pagesError,
        terms: termsError,
      })
    }
  }

  // post request to PDF service
  const postPdfRequest = async (
    apiUrl,
    objectId,
    email,
    pages,
    captchaToken
  ) => {
    const { from, to } = pages.all
      ? { from: startAndEndCanvas.start, to: startAndEndCanvas.end }
      : pages

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          astorId: objectId,
          email: email,
          from: from,
          to: to,
          captchaToken
        }),
      })

      if (response.ok) {
        return await response.json()
      } else {
        switch (response.status) {
          case 400:
            throw new Error('Ungültige Bestellung.')
          case 401:
            throw new Error('Bei Ihrer Bestellung ist ein Fehler aufgetreten.')
          case 403:
            throw new Error('Bei Ihrer Bestellung ist ein Fehler aufgetreten.')
          case 404:
            throw new Error('Objekt nicht gefunden.')
          case 409:
            throw new Error(
              'Die Bestellung konnte nicht abgeschlossen werden.'
            )
          case 500:
            throw new Error('Leider ist ein Problem aufgetreten, bitte probieren Sie es zu einem späteren Zeitpunkt wieder.')
          default:
            throw new Error(`Fehler: ${response.status}`)
        }
      }
    } catch (error) {
      console.error('Error in postPdfRequest');
      throw error
    }
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    setErrors({ ...errors, email: '' })
  }

  const handlePagesChange = (e) => {
    const { name, value } = e.target
    setPages({ ...pages, [name]: value })
    setErrors({ ...errors, pages: '' })
  }

  const handleTermsChange = (e) => {
    setIsTermsAccepted(e.target.checked)
    setErrors({ ...errors, terms: '' })
  }

  const handlePagesAllChange = () => {
    setPages({
      all: true,
      from: startAndEndCanvas.start,
      to: startAndEndCanvas.end,
    })
    setErrors({ ...errors, pages: '' })
  }

  const handlePagesSomeChange = () =>
    setPages({ all: false, from: pages.from, to: pages.to })

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  const validatePages = (pages) => {
    if (pages.all) return true
    const from = parseInt(pages.from, 10)
    const to = parseInt(pages.to, 10)
    const start = parseInt(startAndEndCanvas.start, 10)
    const end = parseInt(startAndEndCanvas.end, 10)
    return pages.from && pages.to && from <= to && from >= start && to <= end
  }

  const validateTerms = (terms) => terms

  const handleCaptchaVerify = (token) => {
    setIsCaptchaValid(true)
    setCaptchaToken(token)
  }

  const handleCaptchaError = () => {
    setIsCaptchaValid(false)
    setCaptchaToken(null)
  }

  return (
    <div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <PageSelection
            pages={pages}
            onAllChange={handlePagesAllChange}
            onSomeChange={handlePagesSomeChange}
            onRangeChange={handlePagesChange}
            error={errors.pages}
            numberOfScans={numberOfScans}
            startAndEndCanvas={startAndEndCanvas}
          />
          <hr />

          <TermsCheckbox
            isTermsAccepted={isTermsAccepted}
            onChange={handleTermsChange}
            error={errors.terms}
          />
          <hr />

          <EmailInput
            email={email}
            handleEmailChange={handleEmailChange}
            error={errors.email}
          />
          <hr />

          <Captcha
            onVerify={handleCaptchaVerify}
            onError={handleCaptchaError}
            onExpire={() => {
              setIsCaptchaValid(false)
              setCaptchaToken(null)
            }}
            config={config}
          />

          <button
            type="submit"
            className="button primary hollow"
            disabled={
              !isCaptchaValid || errors.email || errors.pages || errors.terms
            }
          >
            Auftrag absenden
          </button>
        </form>
      )}
    </div>
  )
}

export default Form
